import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";

const WAIT_IMG = "https://images.unsplash.com/photo-1656343863356-a5efa134a8e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx3b21hbiUyMHN0cmV0Y2hpbmclMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzg1OTU2NDAyfDA&ixlib=rb-4.1.0&q=85";

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | joined | already
  const [loading, setLoading] = useState(false);

  // Launch date: December 1, 2026.
  const launchDate = new Date("2026-12-01T00:00:00Z");
  const now = new Date();
  const daysToLaunch = Math.max(0, Math.ceil((launchDate - now) / (1000 * 60 * 60 * 24)));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", { email, source: "landing" });
      setStatus(data.already_on_list ? "already" : "joined");
      toast.success(data.already_on_list ? "You're already on the list." : "You're in. We'll write when the door opens.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center"><Logo size={80} /></Link>
        <Link to="/" className="text-sm text-stone-600 hover:text-[hsl(var(--foreground))]" data-testid="waitlist-back">Back to preview</Link>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-8 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="label-eyebrow mb-6 text-[hsl(var(--primary))]" data-testid="waitlist-countdown">
            Pre-order · Founding members · <span className="text-[hsl(var(--foreground))]">launching December 1, 2026 · in ~{daysToLaunch} {daysToLaunch === 1 ? "day" : "days"}</span>
          </div>
          <h1 className="font-serif text-[3.5rem] md:text-[5.5rem] lg:text-[6.5rem] leading-[0.95] tracking-[-0.03em]">
            The <span className="font-script text-[hsl(var(--primary))] font-normal">door</span> opens December 1, 2026.
            <br />
            Be first <em className="italic text-[hsl(var(--primary))]">in</em>.
          </h1>
          <p className="mt-10 max-w-xl text-lg leading-relaxed text-stone-700">
            <em className="italic">Energy Awakens</em> launches December 1, 2026. Leave your email and we'll write
            the moment the door opens — plus you'll lock in <strong className="text-[hsl(var(--foreground))]">$9.99 per month for life</strong>.
          </p>

          {status === "idle" ? (
            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg" data-testid="waitlist-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email"
                placeholder="you@yourself.com"
                className="flex-1 bg-transparent border-0 border-b-2 border-[hsl(var(--foreground))] py-3 no-shadow-focus text-lg"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit"
                className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-8 py-4 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60"
              >
                {loading ? "…" : "Save my spot"} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </form>
          ) : (
            <div className="mt-10 border border-[hsl(var(--primary))] p-6 max-w-lg flex gap-4" data-testid="waitlist-confirmed">
              <CheckCircle2 className="w-6 h-6 text-[hsl(var(--primary))] shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <div className="font-serif text-2xl mb-2">
                  {status === "already" ? "You're already on the list." : "You're in."}
                </div>
                <p className="text-sm text-stone-600 leading-relaxed">
                  We'll write when the door opens. Your <strong className="text-[hsl(var(--foreground))]">$9.99 per month for life</strong> rate is reserved to <em>{email}</em> — it locks in when you subscribe.
                </p>
              </div>
            </div>
          )}

          <div className="mt-14 border-t border-border pt-8 grid grid-cols-3 gap-8 max-w-lg">
            <Perk icon="✳" label="Launch alert" body="First email when we go live." />
            <Perk icon="✳" label="$9.99 / mo for life" body="Your founding rate, locked in." />
            <Perk icon="✳" label="Founding member" body="A quiet badge on your profile." />
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="overflow-hidden aspect-[4/5]">
            <img src={WAIT_IMG} alt="A quiet moment in warm light" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-[hsl(var(--background))] border border-border p-5 max-w-[260px]">
            <Sparkles className="w-5 h-5 text-[hsl(var(--primary))] mb-3" strokeWidth={1.25} />
            <p className="font-serif italic text-lg leading-snug">
              "Not another wellness sprint. Just a soft door, opening on time."
            </p>
            <p className="text-xs text-stone-500 mt-2">— From the notes</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-stone-500">
          <span className="font-serif italic">Energy Awakens — coming soon.</span>
          <span>© {new Date().getFullYear()} · By <a href="https://satoriom.com" target="_blank" rel="noreferrer" className="underline hover:text-[hsl(var(--primary))]">Satori OM</a>.</span>
        </div>
      </footer>
    </div>
  );
}

function Perk({ icon, label, body }) {
  return (
    <div>
      <div className="text-[hsl(var(--primary))] font-serif text-2xl mb-2">{icon}</div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-xs text-stone-600 leading-relaxed">{body}</div>
    </div>
  );
}
